import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { marketplaceApi, shareApi } from "../../api/endpoints.js";
import type { PerfilConContacto, PerfilPublico } from "../../api/types.js";
import { useAuth } from "../../auth/AuthContext.js";
import { AdultScreen, AdultHeader, Content } from "../../components/Layout.js";
import { Card } from "../../components/Card.js";
import { Button } from "../../components/Button.js";
import { Field, TextInput, ErrorBanner, InfoBanner } from "../../components/Form.js";
import { Loader } from "../../components/Loader.js";
import { mensajeDeError } from "../../hooks/useQuery.js";
import { useT } from "../../i18n/index.js";

export function MarketplacePage() {
  const t = useT();
  const { usuario } = useAuth();
  const [searchParams] = useSearchParams();

  // Modo "compartir pre-informe": el padre llega desde su reporte con la
  // batería/niño en la query. Con ambos presentes y sesión de padre, cada
  // resultado ofrece enviar la invitación de revisión a ese terapeuta.
  const compartirBateriaId = searchParams.get("compartir");
  const compartirNinoId = searchParams.get("nino");
  const modoCompartir =
    !!compartirBateriaId && !!compartirNinoId && usuario?.rol === "padre";

  // Estado del envío de invitación por terapeuta.
  const [compartiendoId, setCompartiendoId] = useState<string | null>(null);
  const [compartidoId, setCompartidoId] = useState<Record<string, boolean>>({});
  const [compartirError, setCompartirError] = useState<Record<string, string>>({});

  const [pais, setPais] = useState("");
  const [ciudad, setCiudad] = useState("");
  const [especialidad, setEspecialidad] = useState("");
  const [resultados, setResultados] = useState<PerfilPublico[]>([]);
  const [buscado, setBuscado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [buscando, setBuscando] = useState(false);

  // Contacto por perfil (solo autenticado).
  const [contactos, setContactos] = useState<Record<string, PerfilConContacto>>({});
  const [contactoCargando, setContactoCargando] = useState<string | null>(null);
  const [contactoError, setContactoError] = useState<Record<string, string>>({});

  async function onBuscar(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    setBuscando(true);
    try {
      const q = {
        ...(pais.trim() ? { pais: pais.trim() } : {}),
        ...(ciudad.trim() ? { ciudad: ciudad.trim() } : {}),
        ...(especialidad.trim() ? { especialidad: especialidad.trim() } : {}),
      };
      setResultados(await marketplaceApi.buscar(q));
      setBuscado(true);
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setBuscando(false);
    }
  }

  async function compartir(terapeutaId: string) {
    if (!compartirBateriaId || !compartirNinoId) return;
    setCompartiendoId(terapeutaId);
    setCompartirError((prev) => {
      const next = { ...prev };
      delete next[terapeutaId];
      return next;
    });
    try {
      await shareApi.compartir({
        bateriaId: compartirBateriaId,
        childId: compartirNinoId,
        terapeutaId,
      });
      setCompartidoId((prev) => ({ ...prev, [terapeutaId]: true }));
    } catch (e) {
      setCompartirError((prev) => ({ ...prev, [terapeutaId]: mensajeDeError(e) }));
    } finally {
      setCompartiendoId(null);
    }
  }

  async function verContacto(id: string) {
    setContactoCargando(id);
    setContactoError((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    try {
      const ficha = await marketplaceApi.ficha(id);
      setContactos((prev) => ({ ...prev, [id]: ficha }));
    } catch (e) {
      setContactoError((prev) => ({ ...prev, [id]: mensajeDeError(e) }));
    } finally {
      setContactoCargando(null);
    }
  }

  return (
    <AdultScreen>
      <AdultHeader title={t("marketplace.title")} />
      <Content>
        {modoCompartir ? <InfoBanner>{t("share.banner")}</InfoBanner> : null}
        <Card>
          <form onSubmit={onBuscar} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {error ? <ErrorBanner message={error} /> : null}
            <Field label="País">
              <TextInput value={pais} onChange={(e) => setPais(e.target.value)} />
            </Field>
            <Field label="Ciudad">
              <TextInput value={ciudad} onChange={(e) => setCiudad(e.target.value)} />
            </Field>
            <Field label="Especialidad">
              <TextInput value={especialidad} onChange={(e) => setEspecialidad(e.target.value)} />
            </Field>
            <Button type="submit" block disabled={buscando}>
              {buscando ? t("common.loading") : "Buscar"}
            </Button>
          </form>
        </Card>

        {resultados.map((p) => {
          const contacto = contactos[p.id];
          const errContacto = contactoError[p.id];
          return (
            <Card key={p.id}>
              <h2>{p.nombreProfesional}</h2>
              <p>
                {p.ciudad}, {p.pais}
              </p>
              {p.especialidades.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                  {p.especialidades.map((esp, i) => (
                    <span
                      key={`${esp}-${i}`}
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        padding: "2px 10px",
                        borderRadius: "var(--radius-pill)",
                        background: "var(--sky-deep)",
                        color: "var(--lagoon-ink)",
                      }}
                    >
                      {esp}
                    </span>
                  ))}
                </div>
              ) : null}
              {p.bio ? <p style={{ marginTop: 8 }}>{p.bio}</p> : null}

              {modoCompartir ? (
                <div style={{ marginTop: 10 }}>
                  {compartidoId[p.terapeutaId] ? (
                    <InfoBanner>{t("share.sent")}</InfoBanner>
                  ) : (
                    <>
                      <Button
                        variant="primary"
                        disabled={compartiendoId === p.terapeutaId}
                        onClick={() => void compartir(p.terapeutaId)}
                      >
                        {compartiendoId === p.terapeutaId ? t("share.sharing") : t("share.cta")}
                      </Button>
                      {compartirError[p.terapeutaId] ? (
                        <InfoBanner tone="warn">{compartirError[p.terapeutaId]}</InfoBanner>
                      ) : null}
                    </>
                  )}
                </div>
              ) : null}

              <div style={{ marginTop: 10 }}>
                {contacto ? (
                  <div style={{ fontSize: 14 }}>
                    {contacto.contactoEmail ? <div>✉ {contacto.contactoEmail}</div> : null}
                    {contacto.contactoWhatsapp ? <div>WhatsApp: {contacto.contactoWhatsapp}</div> : null}
                    {!contacto.contactoEmail && !contacto.contactoWhatsapp ? (
                      <span style={{ color: "var(--ink-soft)" }}>Sin datos de contacto públicos.</span>
                    ) : null}
                  </div>
                ) : (
                  <>
                    <Button
                      variant="sun"
                      disabled={contactoCargando === p.id}
                      onClick={() => void verContacto(p.id)}
                    >
                      {t("marketplace.contact")}
                    </Button>
                    {errContacto ? (
                      <InfoBanner tone="warn">
                        {usuario
                          ? errContacto
                          : "Inicia sesión para ver los datos de contacto."}
                      </InfoBanner>
                    ) : null}
                  </>
                )}
              </div>
            </Card>
          );
        })}

        {buscando ? <Loader label={t("common.loading")} /> : null}
        {buscado && !buscando && resultados.length === 0 ? (
          <InfoBanner>No se encontraron terapeutas con esos filtros.</InfoBanner>
        ) : null}

        <Link to="/" style={{ fontWeight: 700 }}>
          ← {t("common.back")}
        </Link>
      </Content>
    </AdultScreen>
  );
}
